import { DateHelper } from './DateHelper';

interface EmailParts {
	localPart: string;
	domain: string;
}

interface EmailValidationConfig {
	minLocalPartLength: number;
	maxLocalPartLength: number;
	maxDomainLength: number;
	allowedLocalPartChars: RegExp;
	allowedDomainLabelChars: RegExp;
	minTldLength: number;
	maxTldLength: number;
}

const EMAIL_CONFIG: EmailValidationConfig = {
	minLocalPartLength: 1,
	maxLocalPartLength: 64,
	maxDomainLength: 255,
	allowedLocalPartChars: /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+$/,
	// Domain labels are validated *after* splitting by '.', so we don't allow '.' here.
	allowedDomainLabelChars: /^[a-zA-Z0-9-]+$/,
	minTldLength: 2,
	maxTldLength: 63,
};

export class Validations {
	static isDateGreaterThanYesterday(value: string) {
		const parsedDate = DateHelper.parseDate(value);

		const currentDate = new Date();
		currentDate.setDate(currentDate.getDate() - 1);

		return parsedDate >= currentDate;
	}
	static isArrayFullFilled(value: any[]) {
		return value && value.length > 0;
	}
	/**
	 * Check if email is valid
	 * @param email
	 * @returns
	 */
	private static validateLocalPart(localPart: string): boolean {
		if (!localPart) return false;
		if (
			localPart.length < EMAIL_CONFIG.minLocalPartLength ||
			localPart.length > EMAIL_CONFIG.maxLocalPartLength
		)
			return false;
		if (localPart.startsWith('.') || localPart.endsWith('.')) return false;
		if (localPart.includes('..')) return false;
		return EMAIL_CONFIG.allowedLocalPartChars.test(localPart);
	}

	private static validateDomainLabel(label: string): boolean {
		if (!label) return false;
		// RFC: each label up to 63 chars
		if (label.length > EMAIL_CONFIG.maxTldLength) return false;
		// labels cannot start/end with hyphen
		if (label.startsWith('-') || label.endsWith('-')) return false;
		return EMAIL_CONFIG.allowedDomainLabelChars.test(label);
	}

	private static validateTldLabel(tld: string): boolean {
		if (!tld) return false;
		if (tld.length < EMAIL_CONFIG.minTldLength || tld.length > EMAIL_CONFIG.maxTldLength)
			return false;

		const normalized = tld.toLowerCase();

		if (normalized.startsWith('xn--')) {
			if (!this.validateDomainLabel(normalized)) return false;

			return normalized.length > 'xn--'.length;
		}

		return /^[a-z]+$/.test(normalized);
	}

	private static validateDomainPart(domain: string): boolean {
		if (!domain) return false;
		if (domain.length > EMAIL_CONFIG.maxDomainLength) return false;
		if (domain.startsWith('.') || domain.endsWith('.')) return false;
		if (domain.includes('..')) return false;

		const parts = domain.split('.');
		if (parts.length < 2) return false;

		const tld = parts[parts.length - 1];
		if (!tld) return false;
		if (!this.validateTldLabel(tld)) return false;

		return parts.every((part) => this.validateDomainLabel(part));
	}

	private static parseEmail(email: string): EmailParts | null {
		if (!email || typeof email !== 'string') return null;

		const trimmed = email.trim().toLowerCase();
		if (!trimmed || trimmed.includes(' ')) return null;

		const atIndex = trimmed.indexOf('@');
		const lastAtIndex = trimmed.lastIndexOf('@');
		if (atIndex === -1 || atIndex !== lastAtIndex) return null;

		return {
			localPart: trimmed.slice(0, atIndex),
			domain: trimmed.slice(atIndex + 1),
		};
	}

	public static validateEmail(email: string): boolean {
		const parts = this.parseEmail(email);
		if (!parts) return false;

		const isValidLocalPart = this.validateLocalPart(parts.localPart);
		if (!isValidLocalPart) return false;

		const isValidDomain = this.validateDomainPart(parts.domain);
		if (!isValidDomain) return false;

		const finalCheck =
			/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
		return finalCheck.test(email.trim().toLowerCase());
	}

	/**
	 * Validate a CPF Document
	 * @param cpf
	 * @returns
	 */
	public static validateCpf(cpf: string) {
		if (!cpf || cpf.trim().length === 0) return false;

		// Remove caracteres inválidos do CPF
		cpf = cpf.replace(/[^\d]/g, '');

		// Verifica se o CPF possui 11 dígitos
		if (cpf.length !== 11) {
			return false;
		}

		// Verifica se todos os dígitos são iguais
		if (/^(\d)\1{10}$/.test(cpf)) {
			return false;
		}

		// Verifica se o primeiro dígito verificador está correto
		let sum = 0;
		for (let i = 0; i < 9; i++) {
			sum += parseInt(cpf.charAt(i)) * (10 - i);
		}
		let remainder = 11 - (sum % 11);
		let digit = remainder > 9 ? 0 : remainder;
		if (digit !== parseInt(cpf.charAt(9))) {
			return false;
		}

		// Verifica se o segundo dígito verificador está correto
		sum = 0;
		for (let i = 0; i < 10; i++) {
			sum += parseInt(cpf.charAt(i)) * (11 - i);
		}
		remainder = 11 - (sum % 11);
		digit = remainder > 9 ? 0 : remainder;
		if (digit !== parseInt(cpf.charAt(10))) {
			return false;
		}

		return true;
	}

	/**
	 * Validate a CNPJ
	 * @param value
	 * @returns
	 */
	public static validateCnpj(value: string) {
		if (!value) return false;

		const isString = typeof value === 'string';
		const validTypes = isString || Number.isInteger(value) || Array.isArray(value);

		if (!validTypes) return false;

		if (isString && value.length > 18) return false;

		const match = value.toString().match(/\d/g);
		const numbers = Array.isArray(match) ? match.map(Number) : [];

		if (numbers.length !== 14) return false;

		const items = [...new Set(numbers)];
		if (items.length === 1) return false;

		const calc = (x: number) => {
			const slice = numbers.slice(0, x);
			let factor = x - 7;
			let sum = 0;

			for (let i = x; i >= 1; i--) {
				const n = slice[x - i] ?? 0;
				sum += n * factor--;
				if (factor < 2) factor = 9;
			}

			const result = 11 - (sum % 11);

			return result > 9 ? 0 : result;
		};

		const digits = numbers.slice(12);

		const digit0 = calc(12);
		if (digit0 !== digits[0]) return false;

		const digit1 = calc(13);
		return digit1 === digits[1];
	}

	/**
	 *
	 * @param dateStr
	 * @returns
	 */
	public static isDateValid(dateStr: string): boolean {
		if (!dateStr || dateStr.length == 0) return false;
		try {
			const date = DateHelper.parseDate(dateStr);

			return date instanceof Date && !isNaN(date.getTime());
		} catch (error) {
			return false;
		}
	}

	public static calculateYearsDiff(dateStr: string): number {
		const currentDate = new Date();
		const parsedDate = DateHelper.parseDate(dateStr);
		const diffInMs = currentDate.getTime() - parsedDate.getTime();
		const yearsDiff = diffInMs / (1000 * 60 * 60 * 24 * 365);

		return yearsDiff;
	}

	/**
	 * Menor ano aceito numa data digitada por pessoa. Existe por causa da data mínima do .NET
	 * (`0001-01-01`), que o backend devolve quando o campo está nulo: o AppInputDate esconde esse
	 * valor do VISUAL mas mantém no modelo, e aí `calculateYearsDiff` dá ~2000 anos — passando
	 * folgado por qualquer `>= 18`. Sem este piso, locatário sem nascimento aparece com campo em
	 * branco e mesmo assim avança.
	 */
	private static readonly MIN_PLAUSIBLE_YEAR = 1900;

	/** Se a data é de uma pessoa real, e não a data mínima do .NET / lixo de importação. */
	public static isPlausiblePersonDate(dateStr: string): boolean {
		if (!dateStr) return false;
		const parsed = DateHelper.parseDate(dateStr);
		if (!parsed || Number.isNaN(parsed.getTime())) return false;
		return parsed.getFullYear() >= Validations.MIN_PLAUSIBLE_YEAR;
	}

	public static isOver18YearsOld(dateStr: string): boolean {
		if (!Validations.isPlausiblePersonDate(dateStr)) return false;
		return Validations.calculateYearsDiff(dateStr) >= 18;
	}

	public static isOver65YearsOld(dateStr: string): boolean {
		return Validations.calculateYearsDiff(dateStr) >= 65;
	}

	public static isMinimalOpeningDate(dateStr: string): boolean {
		return Validations.calculateYearsDiff(dateStr) >= 2;
	}

	/**
	 * Valida se o DDD é válido (11 a 99)
	 */
	private static validateDDD(ddd: string): boolean {
		const dddNum = parseInt(ddd);
		return dddNum >= 11 && dddNum <= 99;
	}

	/**
	 * Valida Celular: (XX) 9XXXX-XXXX
	 * Deve ter 11 dígitos, DDD válido e começar com 9
	 */
	public static validateMobile(value: string): boolean {
		const digits = value.replace(/\D/g, '');

		if (digits.length !== 11) return false;

		const ddd = digits.substring(0, 2);
		const firstDigit = digits.substring(2, 3);

		// Verifica DDD e se começa com 9
		if (!this.validateDDD(ddd)) return false;
		if (firstDigit !== '9') return false;

		// Verifica dígitos repetidos (ex: 11 99999-9999 é válido, mas 11 00000-0000 não)
		if (/^(\d)\1{10}$/.test(digits)) return false;

		return true;
	}

	/**
	 * Valida Telefone Fixo: (XX) XXXX-XXXX
	 * Deve ter 10 dígitos, DDD válido e o primeiro dígito do número entre 2 e 8
	 */
	public static validateLandline(value: string): boolean {
		const digits = value.replace(/\D/g, '');

		if (digits.length !== 10) return false;

		const ddd = digits.substring(0, 2);
		const firstDigit = digits.substring(2, 3);

		// Verifica DDD e faixa de fixo (2 a 8 - não existe fixo começando com 0, 1 ou 9)
		if (!this.validateDDD(ddd)) return false;
		if (['0', '1', '9'].includes(firstDigit)) return false;

		if (/^(\d)\1{9}$/.test(digits)) return false;

		return true;
	}

	/**
	 * Validação genérica (mantida para compatibilidade, mas direcionando)
	 */
	public static validatePhoneNumber(value: string, type: 'Cel' | 'Tel' = 'Cel'): boolean {
		if (!value) return false;
		if (type === 'Cel') return this.validateMobile(value);
		if (type === 'Tel') return this.validateLandline(value);
		return false;
	}

	/**
	 * Should validate if the fullname is valid
	 * If contain more then 2 names
	 * @param fullname
	 */
	public static validateFullname(fullname: string) {
		if (!fullname || fullname.trim().length === 0) return false;

		//regex to validate names
		const regex = /^[a-zA-Z\s]+$/;
		if (!regex.test(fullname)) return false;

		const names = fullname.split(' ');

		return names.length > 1;
	}

	public static validateMaxFinancingTerm(birthDate: string, term: number) {
		const today = new Date();

		const age = DateHelper.diffBetweenDates(today, birthDate, 'years');

		const maxTermInYears = 80.5 - age;
		const maxTermInMonths = maxTermInYears * 12;
		const calculatedTerm = Math.min(maxTermInMonths, term);

		return calculatedTerm;
	}
}

export default Validations;
